import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FaPlus, FaTrash, FaTimes, FaSpinner, FaFileAlt, FaCalendarAlt, FaUsers,
    FaInfoCircle, FaSave, FaToggleOn, FaToggleOff, FaRedo
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { useToast } from '../utils/toastContext';
import { formatApiError } from '../utils/apiErrorFormatter';
import { describeSubmissionPolicy, getFormTypeLabel } from '../utils/formTypes';
import '../styles/ManageCurriculums.css';
import '../styles/ManageRanks.css';

const EMPTY_DRAFT = {
    form_template_id: '',
    schedule: { type: 'one_time', start_date: '', end_date: '', repeat_every_days: '' },
    target_type: 'form_type',
    target_user_ids: '',
    target_filters: { account_types: [], ranks: '' }
};

const TARGET_TYPE_LABELS = {
    form_type: 'Auto by form type',
    filters: 'Filtered audience',
    explicit: 'Specific users'
};

/** `datetime-local` gives "YYYY-MM-DDTHH:mm"; the API wants a full ISO string. */
const toIsoOrNull = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/** Inverse of the above, for populating the editor from a saved schedule. */
const toLocalInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatWindow = (schedule) => {
    if (!schedule?.start_date || !schedule?.end_date) return 'No schedule set';
    const opts = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    const start = new Date(schedule.start_date).toLocaleString('en-GB', opts);
    const end = new Date(schedule.end_date).toLocaleString('en-GB', opts);
    const repeat = schedule.type === 'recurring' && schedule.repeat_every_days
        ? ` • repeats every ${schedule.repeat_every_days} day${schedule.repeat_every_days === 1 ? '' : 's'}`
        : '';
    return `${start} → ${end}${repeat}`;
};

/** Open / upcoming / closed, derived the same way the backend gates submission. */
const getWindowStatus = (schedule) => {
    if (!schedule?.start_date || !schedule?.end_date) return { label: 'No schedule', className: '' };
    const now = new Date();
    const start = new Date(schedule.start_date);
    const end = new Date(schedule.end_date);
    if (now < start) return { label: 'Scheduled', className: 'is-status-upcoming' };
    if (now > end) return { label: 'Closed', className: '' };
    return { label: 'Open now', className: 'is-status-active' };
};

const buildSchedulePayload = (schedule) => {
    const payload = {
        type: schedule.type,
        start_date: toIsoOrNull(schedule.start_date),
        end_date: toIsoOrNull(schedule.end_date)
    };
    if (schedule.type === 'recurring') {
        payload.repeat_every_days = Number(schedule.repeat_every_days) || null;
    }
    return payload;
};

/** Shared validation for both the "link" and "edit schedule" flows. */
const validateSchedule = (schedule) => {
    if (!schedule.start_date) return 'Set a start date and time for the form.';
    if (!schedule.end_date) return 'Set an end date and time for the form.';
    if (new Date(schedule.start_date) >= new Date(schedule.end_date)) {
        return 'The end date must be after the start date.';
    }
    if (schedule.type === 'recurring' && !(Number(schedule.repeat_every_days) > 0)) {
        return 'Recurring forms need a positive "repeat every N days" value.';
    }
    return null;
};

const parseList = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

/**
 * Manage the forms attached to an EXISTING curriculum.
 *
 * Talks to the real curriculum-form endpoints, which nothing in the frontend
 * called before, so forms could only ever be attached while first creating a
 * curriculum:
 *   GET    /curriculum/{id}/forms
 *   POST   /curriculum/{id}/add_form
 *   PUT    /curriculum/{id}/update_form/{templateId}
 *   DELETE /curriculum/{id}/remove_form/{templateId}
 */
const CurriculumFormsPanel = ({ curriculumId, curriculumName }) => {
    const { showSuccess, showError } = useToast();

    const [linkedForms, setLinkedForms] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(EMPTY_DRAFT.schedule);

    const loadForms = useCallback(async () => {
        if (!curriculumId) return;
        try {
            setLoading(true);
            const [formsRes, templatesRes] = await Promise.all([
                axiosInstance.get(`/curriculum/${curriculumId}/forms`),
                axiosInstance.get('/form-builder/templates/')
            ]);
            setLinkedForms(Array.isArray(formsRes.data?.forms) ? formsRes.data.forms : []);
            const allTemplates = Array.isArray(templatesRes.data) ? templatesRes.data : [];
            setTemplates(allTemplates.filter((template) => template.is_active !== false));
        } catch (err) {
            showError(formatApiError(err, 'Failed to load curriculum forms'));
        } finally {
            setLoading(false);
        }
        // showError is stable enough in practice; re-running on it would refetch on every toast.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [curriculumId]);

    useEffect(() => { loadForms(); }, [loadForms]);

    const linkedIds = useMemo(
        () => new Set(linkedForms.map((form) => form.form_template_id)),
        [linkedForms]
    );
    const availableTemplates = useMemo(
        () => templates.filter((template) => !linkedIds.has(template._id)),
        [templates, linkedIds]
    );
    const selectedTemplate = useMemo(
        () => templates.find((template) => template._id === draft.form_template_id) || null,
        [templates, draft.form_template_id]
    );

    const openCount = linkedForms.filter((form) => getWindowStatus(form.schedule).label === 'Open now').length;

    const resetDraft = () => {
        setDraft(EMPTY_DRAFT);
        setShowAdd(false);
    };

    const handleLink = async () => {
        if (!draft.form_template_id) {
            showError('Choose a form template to attach.');
            return;
        }
        const scheduleError = validateSchedule(draft.schedule);
        if (scheduleError) {
            showError(scheduleError);
            return;
        }

        const explicitIds = parseList(draft.target_user_ids);
        if (draft.target_type === 'explicit' && explicitIds.length === 0) {
            showError('List at least one user ID for specific-user targeting.');
            return;
        }

        const rankList = parseList(draft.target_filters.ranks);
        const hasFilters = draft.target_filters.account_types.length > 0 || rankList.length > 0;
        if (draft.target_type === 'filters' && !hasFilters) {
            showError('Choose at least one account type or rank for filtered targeting.');
            return;
        }

        const payload = {
            form_template_id: draft.form_template_id,
            schedule_data: buildSchedulePayload(draft.schedule),
            target_type: draft.target_type,
            target_user_ids: draft.target_type === 'explicit' ? explicitIds : null,
            target_filters: draft.target_type === 'filters'
                ? {
                    account_types: draft.target_filters.account_types.length ? draft.target_filters.account_types : null,
                    ranks: rankList.length ? rankList : null
                }
                : null
        };

        try {
            setSaving(true);
            await axiosInstance.post(`/curriculum/${curriculumId}/add_form`, payload);
            showSuccess('Form attached to curriculum');
            resetDraft();
            await loadForms();
        } catch (err) {
            showError(formatApiError(err, 'Failed to attach form'));
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (form) => {
        try {
            setBusyId(form.form_template_id);
            await axiosInstance.put(
                `/curriculum/${curriculumId}/update_form/${form.form_template_id}`,
                { is_active: !form.is_active }
            );
            showSuccess(form.is_active ? 'Form paused' : 'Form activated');
            await loadForms();
        } catch (err) {
            showError(formatApiError(err, 'Failed to update form'));
        } finally {
            setBusyId(null);
        }
    };

    const handleRemove = async (form) => {
        if (!window.confirm(`Remove "${form.template_name}" from this curriculum?`)) return;
        try {
            setBusyId(form.form_template_id);
            await axiosInstance.delete(`/curriculum/${curriculumId}/remove_form/${form.form_template_id}`);
            showSuccess('Form removed from curriculum');
            await loadForms();
        } catch (err) {
            showError(formatApiError(err, 'Failed to remove form'));
        } finally {
            setBusyId(null);
        }
    };

    const startEditing = (form) => {
        setEditingId(form.form_template_id);
        setEditDraft({
            type: form.schedule?.type || 'one_time',
            start_date: toLocalInput(form.schedule?.start_date),
            end_date: toLocalInput(form.schedule?.end_date),
            repeat_every_days: form.schedule?.repeat_every_days || ''
        });
    };

    const handleSaveSchedule = async (form) => {
        const scheduleError = validateSchedule(editDraft);
        if (scheduleError) {
            showError(scheduleError);
            return;
        }
        try {
            setBusyId(form.form_template_id);
            await axiosInstance.put(
                `/curriculum/${curriculumId}/update_form/${form.form_template_id}`,
                { schedule_data: buildSchedulePayload(editDraft) }
            );
            showSuccess('Schedule updated');
            setEditingId(null);
            await loadForms();
        } catch (err) {
            showError(formatApiError(err, 'Failed to update schedule'));
        } finally {
            setBusyId(null);
        }
    };

    const renderScheduleFields = (schedule, onChange) => (
        <div className="mc-grid-2">
            <label className="mc-field">Schedule type
                <select value={schedule.type} onChange={(e) => onChange({ ...schedule, type: e.target.value })}>
                    <option value="one_time">One time</option>
                    <option value="recurring">Recurring</option>
                </select>
            </label>
            <label className="mc-field">Opens
                <input
                    type="datetime-local"
                    value={schedule.start_date}
                    onChange={(e) => onChange({ ...schedule, start_date: e.target.value })}
                />
            </label>
            <label className="mc-field">Closes
                <input
                    type="datetime-local"
                    value={schedule.end_date}
                    onChange={(e) => onChange({ ...schedule, end_date: e.target.value })}
                />
            </label>
            {schedule.type === 'recurring' && (
                <label className="mc-field">Repeat every (days)
                    <input
                        type="number"
                        min="1"
                        placeholder="e.g. 7 for weekly"
                        value={schedule.repeat_every_days}
                        onChange={(e) => onChange({ ...schedule, repeat_every_days: e.target.value })}
                    />
                </label>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="mc-editor-panel" style={{ marginTop: '1.25rem', display: 'grid', placeItems: 'center', minHeight: '180px' }}>
                <FaSpinner className="mc-spin" style={{ fontSize: '2rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
            </div>
        );
    }

    return (
        <div className="mc-editor-panel mc-transition-up" style={{ marginTop: '1.25rem' }}>
            <div className="mc-editor-head">
                <div>
                    <h3><FaFileAlt style={{ marginRight: 8 }} /> Forms</h3>
                    <p>
                        {linkedForms.length} attached to {curriculumName || 'this curriculum'}
                        {openCount > 0 ? ` • ${openCount} open right now` : ''}
                    </p>
                </div>
                <div className="mc-editor-actions">
                    <button className="mc-secondary-btn is-on-dark" type="button" onClick={loadForms}>
                        <FaRedo /> Refresh
                    </button>
                    {!showAdd && (
                        <button
                            className="mc-primary-btn"
                            type="button"
                            onClick={() => setShowAdd(true)}
                            disabled={availableTemplates.length === 0}
                            title={availableTemplates.length === 0 ? 'Every published template is already attached' : undefined}
                        >
                            <FaPlus /> Attach Form
                        </button>
                    )}
                </div>
            </div>

            {showAdd && (
                <div className="mc-form-card mc-transition-up" style={{ marginBottom: '1rem' }}>
                    <div className="mc-section-head" style={{ justifyContent: 'space-between' }}>
                        <h4 className="mc-subcard-title" style={{ margin: 0 }}>Attach a form</h4>
                        <button className="mc-secondary-btn" type="button" onClick={resetDraft}>
                            <FaTimes /> Cancel
                        </button>
                    </div>

                    <label className="mc-field" style={{ marginTop: '0.85rem' }}>Form template
                        <select
                            value={draft.form_template_id}
                            onChange={(e) => setDraft({ ...draft, form_template_id: e.target.value })}
                        >
                            <option value="">Choose a form template…</option>
                            {availableTemplates.map((template) => (
                                <option key={template._id} value={template._id}>
                                    {template.name} — {getFormTypeLabel(template.form_type)}
                                    {template.is_published ? '' : ' (draft)'}
                                </option>
                            ))}
                        </select>
                    </label>

                    {selectedTemplate && (
                        <p className="mr-hint" style={{ marginTop: '0.6rem' }}>
                            <FaInfoCircle style={{ marginRight: '0.4rem' }} />
                            {describeSubmissionPolicy(selectedTemplate.form_type, selectedTemplate.allow_multiple_submissions)}
                            {selectedTemplate.is_published ? '' : ' • This template is still a draft — publish it so recipients can open it.'}
                        </p>
                    )}

                    <h4 className="mc-subcard-title" style={{ marginTop: '1rem' }}>
                        <FaCalendarAlt /> Availability window
                    </h4>
                    <p className="mr-hint">
                        The form opens and notifies its audience at the start date, and stops accepting submissions after the end date.
                    </p>
                    {renderScheduleFields(draft.schedule, (schedule) => setDraft({ ...draft, schedule }))}

                    <h4 className="mc-subcard-title" style={{ marginTop: '1rem' }}>
                        <FaUsers /> Audience
                    </h4>
                    <label className="mc-field">Who receives it
                        <select
                            value={draft.target_type}
                            onChange={(e) => setDraft({
                                ...draft,
                                target_type: e.target.value,
                                target_user_ids: '',
                                target_filters: { account_types: [], ranks: '' }
                            })}
                        >
                            <option value="form_type">Auto by form type (students or preceptors)</option>
                            <option value="filters">Filtered — by account type and rank</option>
                            <option value="explicit">Specific users</option>
                        </select>
                    </label>

                    {draft.target_type === 'filters' && (
                        <>
                            <div className="mc-field" style={{ marginTop: '0.85rem' }}>
                                <span>Account types</span>
                                <div style={{ display: 'flex', gap: '1.25rem', paddingTop: '0.35rem' }}>
                                    {['student', 'preceptor'].map((type) => (
                                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontWeight: 500 }}>
                                            <input
                                                type="checkbox"
                                                checked={draft.target_filters.account_types.includes(type)}
                                                onChange={(e) => setDraft({
                                                    ...draft,
                                                    target_filters: {
                                                        ...draft.target_filters,
                                                        account_types: e.target.checked
                                                            ? [...draft.target_filters.account_types, type]
                                                            : draft.target_filters.account_types.filter((t) => t !== type)
                                                    }
                                                })}
                                            />
                                            <span style={{ textTransform: 'capitalize' }}>{type}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <label className="mc-field" style={{ marginTop: '0.85rem' }}>Ranks (comma separated)
                                <input
                                    type="text"
                                    placeholder="e.g. PharmD, SPEP"
                                    value={draft.target_filters.ranks}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        target_filters: { ...draft.target_filters, ranks: e.target.value }
                                    })}
                                />
                            </label>
                        </>
                    )}

                    {draft.target_type === 'explicit' && (
                        <label className="mc-field" style={{ marginTop: '0.85rem' }}>User IDs (comma separated)
                            <textarea
                                rows={3}
                                placeholder="student_1, preceptor_4"
                                value={draft.target_user_ids}
                                onChange={(e) => setDraft({ ...draft, target_user_ids: e.target.value })}
                            />
                        </label>
                    )}

                    {draft.target_type === 'form_type' && (
                        <p className="mr-hint">
                            <FaInfoCircle style={{ marginRight: '0.4rem' }} />
                            Recipients are derived from the form type — student forms go to the curriculum&apos;s students,
                            preceptor forms to its preceptors.
                        </p>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1rem' }}>
                        <button className="mc-secondary-btn" type="button" onClick={resetDraft} disabled={saving}>
                            <FaTimes /> Cancel
                        </button>
                        <button className="mc-primary-btn" type="button" onClick={handleLink} disabled={saving}>
                            {saving ? <FaSpinner className="mc-spin" /> : <FaPlus />} {saving ? 'Attaching…' : 'Attach Form'}
                        </button>
                    </div>
                </div>
            )}

            {linkedForms.length === 0 ? (
                <div className="mc-empty-state">
                    <FaFileAlt />
                    <h3>No forms attached yet</h3>
                    <p>Attach a form template and give it a schedule to start collecting submissions for this curriculum</p>
                </div>
            ) : (
                <div className="mc-cards-grid">
                    {linkedForms.map((form) => {
                        const status = getWindowStatus(form.schedule);
                        const isBusy = busyId === form.form_template_id;
                        const isEditing = editingId === form.form_template_id;

                        return (
                            <div key={form.form_template_id} className="mc-curriculum-card">
                                <div className="mc-curriculum-card__top">
                                    <div>
                                        <h4>{form.template_name}</h4>
                                        <p className="mc-curriculum-card__rank">{getFormTypeLabel(form.form_type)}</p>
                                    </div>
                                    <div className={`mc-status-pill ${form.is_active ? status.className : ''}`}>
                                        {form.is_active ? status.label : 'Paused'}
                                    </div>
                                </div>

                                <div className="mc-curriculum-card__meta">
                                    <span><FaCalendarAlt /> {formatWindow(form.schedule)}</span>
                                    <span><FaUsers /> {TARGET_TYPE_LABELS[form.target_type] || 'Auto by form type'}</span>
                                    <span><FaInfoCircle /> {describeSubmissionPolicy(form.form_type, form.allow_multiple_submissions)}</span>
                                </div>

                                {isEditing ? (
                                    <div style={{ marginTop: '1rem' }}>
                                        {renderScheduleFields(editDraft, setEditDraft)}
                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem' }}>
                                            <button className="mc-primary-btn" type="button" onClick={() => handleSaveSchedule(form)} disabled={isBusy}>
                                                {isBusy ? <FaSpinner className="mc-spin" /> : <FaSave />} Save Schedule
                                            </button>
                                            <button className="mc-secondary-btn" type="button" onClick={() => setEditingId(null)} disabled={isBusy}>
                                                <FaTimes /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                                        <button className="mc-secondary-btn" type="button" onClick={() => startEditing(form)} disabled={isBusy}>
                                            <FaCalendarAlt /> Schedule
                                        </button>
                                        <button className="mc-secondary-btn" type="button" onClick={() => handleToggleActive(form)} disabled={isBusy}>
                                            {form.is_active ? <FaToggleOn /> : <FaToggleOff />} {form.is_active ? 'Pause' : 'Activate'}
                                        </button>
                                        <button className="mc-danger-btn" type="button" onClick={() => handleRemove(form)} disabled={isBusy}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default CurriculumFormsPanel;
